import { useEffect, useState } from "react";
import { getWheelDetails } from "./api";

/**
 * useWheelSegments(wheelId) — resolve a SPECIFIC wheel's own real segments, for
 * a thumbnail that must render THAT wheel's shape. Unlike useUserWheelFrame
 * (which resolves a USER's primary wheel), this is keyed by wheelId and fetches
 * the wheel's own detail record, since the caller here (the Creator Analytics
 * "Your Wheels" table) already has a specific wheel id per row, not a user id.
 *
 * `GET /wheel/user/analytics/` (WheelTable's row source) does not include
 * segment data (see CreatorAnalytics/WheelThumb.jsx's own comment: the endpoint
 * "does not return a portrait image today"; it returns no wheel_segments
 * either), so every row fell back to the generic iYuga-logo WheelThumb with no
 * way to show its real shape. This fetches each row's own wheel detail (the
 * same `getWheelDetails` ViewWheel/the Builder use) to fill that gap.
 *
 * Returns `{ segments, loading }`, NOT a bare segments value — same shape as
 * useUserWheelFrame and for the same reason: `segments === null` must mean
 * ONLY "resolved, this wheel genuinely has none", distinct from "still
 * fetching", so a caller doesn't flash the logo fallback before the real
 * wheel paints.
 *
 * Process-wide cache + concurrency-capped queue, mirroring useUserWheelFrame:
 * a paginated table can mount up to 100 rows in the same tick, so fetches are
 * deduped per wheelId and capped rather than fired as one thundering herd.
 */
const cache = new Map(); // wheelId -> segments[] | null (resolved)
const inflight = new Map(); // wheelId -> Promise

const MAX_CONCURRENT_FETCHES = 6;
let activeFetchCount = 0;
const pendingQueue = []; // () => void, each starts exactly one queued fetch

function pump() {
  while (activeFetchCount < MAX_CONCURRENT_FETCHES && pendingQueue.length > 0) {
    activeFetchCount += 1;
    pendingQueue.shift()();
  }
}

export function _resetWheelSegmentsCache() {
  cache.clear();
  inflight.clear();
  pendingQueue.length = 0;
  activeFetchCount = 0;
}

function fetchOnce(wheelId) {
  if (cache.has(wheelId)) return Promise.resolve(cache.get(wheelId));
  if (inflight.has(wheelId)) return inflight.get(wheelId);

  const promise = new Promise((resolve) => {
    pendingQueue.push(() => {
      // Wrapped in Promise.resolve().then(...) so a caller that mocks/replaces
      // this module without `getWheelDetails` (or any synchronous throw from
      // the request layer) still resolves through the .catch() below instead
      // of throwing out of this effect and crashing the row's render.
      Promise.resolve()
        .then(() => getWheelDetails(wheelId))
        .then((res) => {
          const data = res && res.data && res.data.data;
          const segments =
            data && Array.isArray(data.wheel_segments) && data.wheel_segments.length
              ? data.wheel_segments
              : null;
          cache.set(wheelId, segments);
          resolve(segments);
        })
        .catch(() => {
          // A failed/empty lookup is a clean "no real shape available" — fall
          // back to the existing portrait/logo thumbnail.
          cache.set(wheelId, null);
          resolve(null);
        })
        .finally(() => {
          inflight.delete(wheelId);
          activeFetchCount -= 1;
          pump();
        });
    });
  });
  inflight.set(wheelId, promise);
  pump();
  return promise;
}

export default function useWheelSegments(wheelId) {
  const cachedAtMount = wheelId != null && cache.has(wheelId);
  const [segments, setSegments] = useState(() => (cachedAtMount ? cache.get(wheelId) : null));
  const [loading, setLoading] = useState(() => wheelId != null && !cachedAtMount);

  useEffect(() => {
    let alive = true;
    if (wheelId == null) {
      setSegments(null);
      setLoading(false);
      return undefined;
    }
    if (cache.has(wheelId)) {
      setSegments(cache.get(wheelId));
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    fetchOnce(wheelId).then((segs) => {
      if (alive) {
        setSegments(segs);
        setLoading(false);
      }
    });
    return () => {
      alive = false;
    };
  }, [wheelId]);

  return { segments, loading };
}
